import React, { useState, useEffect, useContext, useCallback } from "react";
import { IoMdAdd } from "react-icons/io";
import { BiEditAlt } from "react-icons/bi";
import { FaRegTrashCan, FaArrowLeft } from "react-icons/fa6";
import { IoClose } from "react-icons/io5";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { AppContext } from "../../../../context/appContext";
import LoadingSpinner from "../../../../libs/LoadingSpinner";
import {
  getSeriesCategories,
  addSeriesCategory,
  updateSeriesCategory,
  deleteSeriesCategory,
  getEpisodesByCategory,
  getEpisodeById,
  addEpisode,
  updateEpisode,
  deleteEpisode,
  getMostWatchedEpisodes,
} from "../../../../libs/api/seriesApi";

const CAT_MODAL = "series_category_modal";
const EP_MODAL = "series_episode_modal";

// Series (e.g. برگزیدگان / The Chosen) → optional seasons → HLS episodes (subtitles come from the HLS source).
// Standalone from the سما تی وی Videos section; placed on the app home via a `series/<id>` home banner.
export default function SeriesManager() {
  const { t, isRTL } = useContext(AppContext);

  // Breadcrumb stack: [0] = root (series list). Each entry is a category we've drilled into.
  const [stack, setStack] = useState([{ id: null, title: t("series.title") || "Series", parentId: undefined }]);
  const current = stack[stack.length - 1];
  const atRoot = current.id === null;

  const [categories, setCategories] = useState([]);
  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(false);

  // ----- Most Watched overview (root level only) -----
  const [mostWatched, setMostWatched] = useState([]);
  const [mwSort, setMwSort] = useState("views"); // 'views' (total) | 'unique'

  // ----- Category (series / season) modal -----
  const [catMode, setCatMode] = useState("add");
  const [catId, setCatId] = useState(null);
  const [catIsRoot, setCatIsRoot] = useState(true); // true => editing a top-level series (geo fields shown)
  const [catTitle, setCatTitle] = useState("");
  const [catDesc, setCatDesc] = useState("");
  const [catImage, setCatImage] = useState(null);
  const [geoRestricted, setGeoRestricted] = useState(false);
  const [geoMode, setGeoMode] = useState("block"); // 'block' = list is denied | 'allow' = only list is allowed
  const [blockedCountries, setBlockedCountries] = useState("US");
  const [geoMessage, setGeoMessage] = useState("");
  const [catSaving, setCatSaving] = useState(false);

  // ----- Episode modal -----
  const [epMode, setEpMode] = useState("add");
  const [epId, setEpId] = useState(null);
  const [epTitle, setEpTitle] = useState("");
  const [epDesc, setEpDesc] = useState("");
  const [epHls, setEpHls] = useState("");
  const [epThumb, setEpThumb] = useState(null);
  const [epExistingThumb, setEpExistingThumb] = useState(null);
  const [epSaving, setEpSaving] = useState(false);

  const fetchLevel = useCallback(async () => {
    setLoading(true);
    try {
      const catRes = await getSeriesCategories(current.id === null ? null : current.id);
      if (catRes?.status === 200) {
        setCategories(catRes.data.data || []);
      }
      if (current.id !== null) {
        const epRes = await getEpisodesByCategory(current.id);
        if (epRes?.status === 200) {
          setEpisodes(epRes.data.data || []);
        }
      } else {
        setEpisodes([]);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || t("messages.somethingWentWrong"));
    } finally {
      setLoading(false);
    }
  }, [current.id, t]);

  useEffect(() => {
    fetchLevel();
  }, [fetchLevel]);

  // Most-watched overview is only shown at the series root; refetch on sort change.
  const fetchMostWatched = useCallback(async () => {
    try {
      const res = await getMostWatchedEpisodes(mwSort, 20);
      if (res?.status === 200) setMostWatched(res.data.data || []);
    } catch (e) {
      // Non-critical overview — leave it empty on failure.
    }
  }, [mwSort]);

  useEffect(() => {
    if (atRoot) fetchMostWatched();
  }, [atRoot, fetchMostWatched]);

  // ---------- Navigation ----------
  const drillInto = (cat) => {
    setStack([...stack, { id: cat._id, title: cat.title, parentId: cat.parentId || null }]);
  };
  const goToCrumb = (index) => setStack(stack.slice(0, index + 1));
  const goBack = () => stack.length > 1 && setStack(stack.slice(0, -1));

  // ---------- Category modal ----------
  const openCategoryModal = (mode, cat = null) => {
    setCatMode(mode);
    if (mode === "edit" && cat) {
      setCatId(cat._id);
      setCatIsRoot(!cat.parentId); // a top-level series has no parent
      setCatTitle(cat.title || "");
      setCatDesc(cat.description || "");
      setGeoRestricted(!!cat.geoRestricted);
      setGeoMode(cat.geoMode === "allow" ? "allow" : "block");
      setBlockedCountries((cat.blockedCountries || ["US"]).join(", "));
      setGeoMessage(cat.geoMessage || "");
    } else {
      setCatId(null);
      setCatIsRoot(atRoot); // adding at root => a new series; inside => a season
      setCatTitle("");
      setCatDesc("");
      setGeoRestricted(false);
      setGeoMode("block");
      setBlockedCountries("US");
      setGeoMessage("");
    }
    setCatImage(null);
    document.getElementById(CAT_MODAL).showModal();
  };

  const submitCategory = async () => {
    if (!catTitle.trim()) {
      toast.error(t("validation.titleRequired") || "Title is required.");
      return;
    }
    const fd = new FormData();
    fd.append("title", catTitle);
    fd.append("description", catDesc || "");
    if (catImage) fd.append("image", catImage);

    if (catMode === "add") {
      // A new top-level series must declare its type; a season inherits it from its parent.
      if (atRoot) fd.append("categoryType", "series");
      else fd.append("parentId", current.id);
    }
    // Geo fields only apply to the top-level series.
    if (catIsRoot) {
      fd.append("geoRestricted", geoRestricted ? "true" : "false");
      fd.append("geoMode", geoMode);
      fd.append("blockedCountries", blockedCountries || "");
      fd.append("geoMessage", geoMessage || "");
    }

    try {
      setCatSaving(true);
      const res = catMode === "add"
        ? await addSeriesCategory(fd)
        : await updateSeriesCategory(catId, fd);
      if (res?.status === 200 || res?.status === 201) {
        toast.success(res?.data?.message || t("messages.savedSuccess") || "Saved");
        document.getElementById(CAT_MODAL).close();
        fetchLevel();
      } else {
        toast.error(res?.data?.message || t("messages.somethingWentWrong"));
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || t("messages.somethingWentWrong"));
    } finally {
      setCatSaving(false);
    }
  };

  const handleDeleteCategory = async (cat) => {
    const result = await Swal.fire({
      title: t("dialogs.areYouSure"),
      text: t("series.deleteCategoryWarning") || "This will also delete its seasons and episodes.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: t("dialogs.yesDelete"),
      cancelButtonText: t("dialogs.cancel"),
    });
    if (!result.isConfirmed) return;
    try {
      const res = await deleteSeriesCategory(cat._id, "delete");
      if (res?.status === 200) {
        toast.success(res?.data?.message || t("messages.deletedSuccess") || "Deleted");
        fetchLevel();
      } else {
        toast.error(res?.data?.message || t("messages.somethingWentWrong"));
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || t("messages.somethingWentWrong"));
    }
  };

  // ---------- Episode modal ----------
  const openEpisodeModal = async (mode, ep = null) => {
    setEpMode(mode);
    if (mode === "edit" && ep) {
      setEpId(ep._id);
      // Fetch full episode to get hlsUrl (not present in the list payload).
      try {
        const res = await getEpisodeById(ep._id);
        const data = res?.data?.data || ep;
        setEpTitle(data.title || "");
        setEpDesc(data.description || "");
        setEpHls(data.hlsUrl || "");
        setEpExistingThumb(typeof data.thumbnailUrl === "string" ? data.thumbnailUrl : null);
      } catch (e) {
        setEpTitle(ep.title || "");
        setEpHls("");
      }
    } else {
      setEpId(null);
      setEpTitle("");
      setEpDesc("");
      setEpHls("");
      setEpExistingThumb(null);
    }
    setEpThumb(null);
    document.getElementById(EP_MODAL).showModal();
  };

  const submitEpisode = async () => {
    if (!epTitle.trim()) {
      toast.error(t("validation.titleRequired") || "Title is required.");
      return;
    }
    if (!epHls.trim()) {
      toast.error(t("series.hlsUrlRequired") || "HLS URL (.m3u8) is required.");
      return;
    }

    const fd = new FormData();
    fd.append("title", epTitle);
    fd.append("description", epDesc || "");
    fd.append("sourceType", "hls");
    fd.append("hlsUrl", epHls.trim());
    fd.append("categoryId", current.id);
    if (epThumb) fd.append("thumbnail", epThumb);

    try {
      setEpSaving(true);
      const res = epMode === "add" ? await addEpisode(fd) : await updateEpisode(epId, fd);
      if (res?.status === 200 || res?.status === 201) {
        toast.success(res?.data?.message || t("messages.savedSuccess") || "Saved");
        document.getElementById(EP_MODAL).close();
        fetchLevel();
      } else {
        toast.error(res?.data?.message || t("messages.somethingWentWrong"));
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || t("messages.somethingWentWrong"));
    } finally {
      setEpSaving(false);
    }
  };

  const handleDeleteEpisode = async (ep) => {
    const result = await Swal.fire({
      title: t("dialogs.areYouSure"),
      text: t("dialogs.cantRevert"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: t("dialogs.yesDelete"),
      cancelButtonText: t("dialogs.cancel"),
    });
    if (!result.isConfirmed) return;
    try {
      const res = await deleteEpisode(ep._id);
      if (res?.status === 200) {
        toast.success(res?.data?.message || t("messages.deletedSuccess") || "Deleted");
        fetchLevel();
      } else {
        toast.error(res?.data?.message || t("messages.somethingWentWrong"));
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || t("messages.somethingWentWrong"));
    }
  };

  const addCategoryLabel = atRoot ? (t("series.addSeries") || "Add Series") : (t("series.addSeason") || "Add Season");
  const childrenLabel = atRoot ? (t("series.seriesList") || "Series") : (t("series.seasons") || "Seasons");

  return (
    <div className="p-6">
      <h2 className="text-2xl font-semibold mb-1">{t("series.title") || "Series (The Chosen)"}</h2>
      <p className="text-sm text-gray-500 mb-4">
        {t("series.subtitle") || "Standalone HLS shows shown on the app home next to سما تی وی."}
      </p>

      {/* Breadcrumb */}
      <div className="bg-white rounded-xl shadow-sm p-4 mb-4 flex items-center gap-2">
        {stack.length > 1 && (
          <button onClick={goBack} className="flex items-center gap-2 text-gray-600 hover:text-gray-800 px-3 py-2 rounded-lg hover:bg-gray-100">
            <FaArrowLeft size={16} />
            <span className="font-medium">{t("common.back")}</span>
          </button>
        )}
        <div className="flex items-center gap-2 text-sm flex-wrap">
          {stack.map((crumb, index) => (
            <React.Fragment key={index}>
              {index > 0 && <span className="text-gray-400">{isRTL ? "←" : "→"}</span>}
              <button
                onClick={() => goToCrumb(index)}
                className={`px-3 py-1 rounded-lg ${index === stack.length - 1 ? "bg-blue-100 text-blue-700 font-semibold" : "text-gray-600 hover:bg-gray-100"}`}
              >
                {crumb.title}
              </button>
            </React.Fragment>
          ))}
        </div>
      </div>

      {loading && <div className="flex justify-center p-10"><LoadingSpinner /></div>}

      {!loading && (
        <>
          {/* Series / Seasons */}
          <div className="bg-white rounded-3xl shadow mb-6">
            <div className="flex justify-between items-center p-6">
              <h3 className="font-semibold text-lg">{childrenLabel}</h3>
              <button onClick={() => openCategoryModal("add")} className="flex items-center gap-2 bg-[#0D6EFD] text-white font-medium px-5 py-2 rounded-xl hover:bg-blue-700">
                <IoMdAdd size={20} /> {addCategoryLabel}
              </button>
            </div>
            <div className="px-6 pb-6">
              {categories.length === 0 ? (
                <p className="text-gray-400 text-sm py-4">{t("series.noItems") || "Nothing here yet."}</p>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {categories.map((cat) => (
                    <div key={cat._id} className="border border-[#eee] rounded-2xl overflow-hidden hover:shadow transition-shadow">
                      <div className="h-28 bg-gray-100 cursor-pointer flex items-center justify-center" onClick={() => drillInto(cat)}>
                        {cat.image ? (
                          <img src={cat.image} alt={cat.title} className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-gray-400 text-sm">{t("series.noImage") || "No image"}</span>
                        )}
                      </div>
                      <div className="p-3">
                        <div className="flex items-center justify-between gap-1">
                          <button onClick={() => drillInto(cat)} className="font-medium text-sm truncate text-left hover:text-blue-600">
                            {cat.title}
                          </button>
                          <div className="flex items-center gap-2 shrink-0">
                            <BiEditAlt size={20} className="cursor-pointer hover:opacity-50" onClick={() => openCategoryModal("edit", cat)} />
                            <FaRegTrashCan size={16} className="cursor-pointer hover:opacity-50 text-red-500" onClick={() => handleDeleteCategory(cat)} />
                          </div>
                        </div>
                        <div className="flex items-center gap-2 mt-2">
                          <span className="text-xs text-gray-500">{cat.videoCount || 0} {t("series.episodes") || "episodes"}</span>
                          {cat.geoRestricted && (
                            <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">
                              {cat.geoMode === "allow"
                                ? (t("series.geoAllowedOnly") || "Only")
                                : (t("series.geoBlocked") || "Geo")}
                              : {(cat.blockedCountries || []).join(",")}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Most Watched overview (root level only) */}
          {atRoot && (
            <div className="bg-white rounded-3xl shadow mb-6">
              <div className="flex justify-between items-center p-6 flex-wrap gap-3">
                <h3 className="font-semibold text-lg">{t("series.mostWatched") || "Most Watched Episodes"}</h3>
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-gray-500">{t("series.sortBy") || "Sort by"}:</span>
                  <button
                    onClick={() => setMwSort("views")}
                    className={`px-3 py-1 rounded-lg ${mwSort === "views" ? "bg-blue-100 text-blue-700 font-semibold" : "text-gray-600 hover:bg-gray-100"}`}
                  >
                    {t("series.totalViews") || "Total"}
                  </button>
                  <button
                    onClick={() => setMwSort("unique")}
                    className={`px-3 py-1 rounded-lg ${mwSort === "unique" ? "bg-emerald-100 text-emerald-700 font-semibold" : "text-gray-600 hover:bg-gray-100"}`}
                  >
                    {t("series.uniqueViews") || "Unique"}
                  </button>
                </div>
              </div>
              <div className="px-6 pb-6">
                {mostWatched.length === 0 ? (
                  <p className="text-gray-400 text-sm py-4">{t("series.noViewsYet") || "No watch data yet."}</p>
                ) : (
                  <div className="w-full">
                    <div className="bg-[#FAFAFA] text-sm font-[600] grid grid-cols-12 border-b border-[#F7F7F9]">
                      <div className={`p-2 py-3 col-span-1 ${isRTL ? "pr-6" : "pl-6"}`}>#</div>
                      <div className="p-2 py-3 col-span-5">{t("video.videoTitle") || "Title"}</div>
                      <div className="p-2 py-3 col-span-3">{t("series.seriesList") || "Series"}</div>
                      <div className="p-2 py-3 col-span-1">{t("series.totalViews") || "Total"}</div>
                      <div className="p-2 py-3 col-span-2">{t("series.uniqueViews") || "Unique"}</div>
                    </div>
                    {mostWatched.map((row, i) => (
                      <div key={row._id} className="grid grid-cols-12 hover:bg-gray-50 border-b border-[#efeff1] text-sm items-center">
                        <div className={`p-2 py-3 col-span-1 text-gray-400 ${isRTL ? "pr-6" : "pl-6"}`}>{i + 1}</div>
                        <div className="p-2 py-3 col-span-5 font-[500] truncate" title={row.title || ""}>{row.title || "-"}</div>
                        <div className="p-2 py-3 col-span-3 text-gray-500 truncate" title={row.categoryTitle || ""}>{row.categoryTitle || "-"}</div>
                        <div className="p-2 py-3 col-span-1 font-semibold text-blue-700" dir="ltr">{(row.views || 0).toLocaleString()}</div>
                        <div className="p-2 py-3 col-span-2 font-semibold text-emerald-700" dir="ltr">{(row.uniqueViews || 0).toLocaleString()}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Episodes (only inside a series/season) */}
          {!atRoot && (
            <div className="bg-white rounded-3xl shadow">
              <div className="flex justify-between items-center p-6">
                <h3 className="font-semibold text-lg">{t("series.episodes") || "Episodes"}</h3>
                <button onClick={() => openEpisodeModal("add")} className="flex items-center gap-2 bg-[#0D6EFD] text-white font-medium px-5 py-2 rounded-xl hover:bg-blue-700">
                  <IoMdAdd size={20} /> {t("series.addEpisode") || "Add Episode"}
                </button>
              </div>
              {episodes.length === 0 ? (
                <p className="text-gray-400 text-sm px-6 pb-6">{t("series.noEpisodes") || "No episodes in this category yet."}</p>
              ) : (
                <div className="w-full">
                  <div className="bg-[#FAFAFA] text-sm font-[600] grid grid-cols-5 border-b border-[#F7F7F9]">
                    <div className={`p-2 py-4 ${isRTL ? "pr-8" : "pl-8"}`}>{t("video.thumbnail") || "Thumbnail"}</div>
                    <div className="p-2 py-4 col-span-2">{t("video.videoTitle") || "Title"}</div>
                    <div className="p-2 py-4">{t("series.views") || "Views"}</div>
                    <div className="p-2 py-4">{t("common.actions")}</div>
                  </div>
                  {episodes.map((ep) => (
                    <div key={ep._id} className="grid grid-cols-5 hover:bg-gray-50 border-b border-[#efeff1] text-sm font-[500] items-center">
                      <div className={`p-2 py-4 ${isRTL ? "pr-8" : "pl-8"}`}>
                        {typeof ep.thumbnailUrl === "string" && ep.thumbnailUrl ? (
                          <img src={ep.thumbnailUrl} alt={ep.title} className="w-20 h-12 object-cover rounded" />
                        ) : (
                          <div className="w-20 h-12 bg-gray-200 rounded flex items-center justify-center text-gray-400 text-xs">N/A</div>
                        )}
                      </div>
                      <div className="p-2 py-4 col-span-2">
                        {ep.title || "-"}
                        <span className="ml-2 text-[10px] bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded uppercase">{ep.sourceType || "youtube"}</span>
                      </div>
                      <div className="p-2 py-4">
                        <div className="flex flex-col gap-1" dir="ltr">
                          <span className="text-[11px] bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full w-fit" title={t("series.totalViewsHint") || "Total plays (replays included)"}>
                            {t("series.totalViews") || "Total"}: {(ep.views || 0).toLocaleString()}
                          </span>
                          <span className="text-[11px] bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full w-fit" title={t("series.uniqueViewsHint") || "Distinct viewers"}>
                            {t("series.uniqueViews") || "Unique"}: {(ep.uniqueViews || 0).toLocaleString()}
                          </span>
                        </div>
                      </div>
                      <div className="p-2 py-4">
                        <div className="flex gap-3 items-center">
                          <BiEditAlt size={22} className="cursor-pointer hover:opacity-50" onClick={() => openEpisodeModal("edit", ep)} />
                          <FaRegTrashCan size={18} className="cursor-pointer hover:opacity-50 text-red-500" onClick={() => handleDeleteEpisode(ep)} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* ---------- Category modal ---------- */}
      <dialog id={CAT_MODAL} className="modal">
        <div className="modal-box min-w-[640px] rounded-2xl p-0">
          <div className="p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="font-semibold text-lg">
                {catMode === "add" ? addCategoryLabel : (catIsRoot ? (t("series.editSeries") || "Edit Series") : (t("series.editSeason") || "Edit Season"))}
              </h3>
              <form method="dialog"><button className="text-xl hover:text-red-500"><IoClose /></button></form>
            </div>

            <label className="block text-sm font-medium mb-2">{t("common.title") || "Title"}</label>
            <input type="text" value={catTitle} onChange={(e) => setCatTitle(e.target.value)} className="w-full mb-4 border rounded-xl p-3 border-[#808080]" dir={isRTL ? "rtl" : "ltr"} />

            <label className="block text-sm font-medium mb-2">{t("common.descriptionOptional") || "Description (optional)"}</label>
            <textarea value={catDesc} onChange={(e) => setCatDesc(e.target.value)} rows="2" className="w-full mb-4 border rounded-xl p-3 border-[#808080]" dir={isRTL ? "rtl" : "ltr"} />

            <label className="block text-sm font-medium mb-2">{t("series.coverImage") || "Cover image"}</label>
            <input type="file" accept="image/*" onChange={(e) => setCatImage(e.target.files[0] || null)} className="w-full mb-4" />

            {catIsRoot && (
              <div className="border-t pt-4 mt-2">
                <label className="flex items-center gap-2 mb-3 cursor-pointer">
                  <input type="checkbox" checked={geoRestricted} onChange={(e) => setGeoRestricted(e.target.checked)} />
                  <span className="text-sm font-medium">{t("series.geoRestrict") || "Restrict by country (block playback)"}</span>
                </label>
                {geoRestricted && (
                  <div>
                    <label className="block text-sm font-medium mb-2">{t("series.geoMode") || "List mode"}</label>
                    <div className="flex flex-col gap-2 mb-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="radio" name="geoMode" value="block" checked={geoMode === "block"} onChange={() => setGeoMode("block")} />
                        <span className="text-sm">{t("series.geoModeBlock") || "Block these countries (everyone else can watch)"}</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="radio" name="geoMode" value="allow" checked={geoMode === "allow"} onChange={() => setGeoMode("allow")} />
                        <span className="text-sm">{t("series.geoModeAllow") || "Allow only these countries (everyone else is blocked)"}</span>
                      </label>
                    </div>

                    <label className="block text-sm font-medium mb-2">
                      {geoMode === "allow"
                        ? (t("series.allowedCountries") || "Allowed countries (ISO codes, comma-separated)")
                        : (t("series.blockedCountries") || "Blocked countries (ISO codes, comma-separated)")}
                    </label>
                    <input type="text" value={blockedCountries} onChange={(e) => setBlockedCountries(e.target.value)} placeholder="US, CA, DE" className="w-full border rounded-xl p-3 border-[#808080]" dir="ltr" />
                    <p className="text-xs text-gray-500 mt-1">{t("series.geoHint") || "Enter one or more ISO country codes separated by commas — e.g. US, CA, DE."}</p>

                    <label className="block text-sm font-medium mb-2 mt-4">{t("series.geoMessage") || "Message for blocked viewers (optional)"}</label>
                    <textarea value={geoMessage} onChange={(e) => setGeoMessage(e.target.value)} rows="2" placeholder={t("series.geoMessageDefault") || "This content is not available in your region."} className="w-full border rounded-xl p-3 border-[#808080]" dir={isRTL ? "rtl" : "ltr"} />
                    <p className="text-xs text-gray-500 mt-1">{t("series.geoMessageHint") || "Shown instead of the player. Leave empty to use the default message."}</p>

                    <p className="text-xs text-gray-500 mt-3">{t("series.geoFailOpenNote") || "Note: viewers whose country cannot be detected are always allowed to watch."}</p>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="modal-action bg-[#fafafa] mt-2 p-6 py-4 justify-end flex">
            <button type="button" disabled={catSaving} onClick={submitCategory} className="btn bg-[#0D6EFD] rounded-xl p-2 px-5 text-white hover:bg-blue-700">
              {catSaving ? <LoadingSpinner /> : catMode === "add" ? t("common.add") : t("common.update")}
            </button>
          </div>
        </div>
      </dialog>

      {/* ---------- Episode modal ---------- */}
      <dialog id={EP_MODAL} className="modal">
        <div className="modal-box min-w-[720px] rounded-2xl p-0">
          <div className="p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="font-semibold text-lg">{epMode === "add" ? (t("series.addEpisode") || "Add Episode") : (t("series.editEpisode") || "Edit Episode")}</h3>
              <form method="dialog"><button className="text-xl hover:text-red-500"><IoClose /></button></form>
            </div>

            <label className="block text-sm font-medium mb-2">{t("video.videoTitle") || "Title"}</label>
            <input type="text" value={epTitle} onChange={(e) => setEpTitle(e.target.value)} placeholder="S1E1" className="w-full mb-4 border rounded-xl p-3 border-[#808080]" dir={isRTL ? "rtl" : "ltr"} />

            <label className="block text-sm font-medium mb-2">{t("series.hlsUrl") || "HLS URL (.m3u8)"}</label>
            <input type="text" value={epHls} onChange={(e) => setEpHls(e.target.value)} placeholder="https://cdn.thechosen.media/.../master_transform.m3u8" className="w-full mb-4 border rounded-xl p-3 border-[#808080]" dir="ltr" />

            <label className="block text-sm font-medium mb-2">{t("series.descriptionOptional") || "Description (optional)"}</label>
            <textarea value={epDesc} onChange={(e) => setEpDesc(e.target.value)} rows="2" className="w-full mb-4 border rounded-xl p-3 border-[#808080]" dir={isRTL ? "rtl" : "ltr"} />

            <label className="block text-sm font-medium mb-2">{t("series.thumbnail") || "Thumbnail image"}</label>
            {epExistingThumb && !epThumb && (
              <img src={epExistingThumb} alt="thumb" className="w-32 h-20 object-cover rounded mb-2" />
            )}
            <input type="file" accept="image/*" onChange={(e) => setEpThumb(e.target.files[0] || null)} className="w-full mb-4" />
          </div>
          <div className="modal-action bg-[#fafafa] mt-2 p-6 py-4 justify-end flex">
            <button type="button" disabled={epSaving} onClick={submitEpisode} className="btn bg-[#0D6EFD] rounded-xl p-2 px-5 text-white hover:bg-blue-700">
              {epSaving ? <LoadingSpinner /> : epMode === "add" ? t("common.add") : t("common.update")}
            </button>
          </div>
        </div>
      </dialog>
    </div>
  );
}
